import { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { sitesApi, type CreateSiteRequest } from '@/api/sites';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import {
  AQUIFER_OPTIONS,
  CONTAMINANT_OPTIONS,
  REGULATORY_PROGRAM_OPTIONS,
  STATUS_OPTIONS,
  US_STATE_OPTIONS,
} from '@/pages/site-fields';
import type { AxiosError } from 'axios';

interface FormState {
  site_identifier: string;
  name: string;
  status: string;
  state: string;
  city: string;
  county: string;
  address: string;
  latitude: string;
  longitude: string;
  aquifer_type: string;
  primary_contaminant: string;
  primary_contaminant_other: string;
  regulatory_program: string;
}

const EMPTY: FormState = {
  site_identifier: '',
  name: '',
  status: 'active',
  state: '',
  city: '',
  county: '',
  address: '',
  latitude: '',
  longitude: '',
  aquifer_type: '',
  primary_contaminant: '',
  primary_contaminant_other: '',
  regulatory_program: '',
};

export function SiteCreate() {
  return (
    <RouteErrorBoundary routeName="Create Site">
      <SiteCreateForm />
    </RouteErrorBoundary>
  );
}

function SiteCreateForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const sitesPath = location.pathname.startsWith('/data-vault') ? '/data-vault/sites' : '/sites';

  const [form, setForm] = useState<FormState>(EMPTY);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [topError, setTopError] = useState<string | null>(null);

  const update = (key: keyof FormState, value: string) => {
    setForm(prev => ({ ...prev, [key]: value }));
    if (fieldErrors[key]) {
      setFieldErrors(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const createMutation = useMutation({
    mutationFn: (payload: CreateSiteRequest) => sitesApi.create(payload),
    onSuccess: (site) => {
      navigate(`${sitesPath}/${site.id}`);
    },
    onError: (error: AxiosError<{ detail?: string }>) => {
      const detail = error.response?.data?.detail || error.message || 'Failed to create site';
      // Backend returns 400 with descriptive detail; surface it where the user
      // can act on it. Identifier-related errors go inline; limit-reached is
      // a top-of-form banner.
      if (/already exists/i.test(detail) || /reserved/i.test(detail)) {
        setFieldErrors({ site_identifier: detail });
        setTopError(null);
      } else if (/limit reached/i.test(detail)) {
        setTopError(detail);
      } else {
        setTopError(detail);
      }
    },
  });

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!form.site_identifier.trim()) errs.site_identifier = 'Required';
    if (!form.name.trim()) errs.name = 'Required';
    if (form.latitude.trim()) {
      const n = parseFloat(form.latitude);
      if (isNaN(n) || n < -90 || n > 90) errs.latitude = 'Must be between -90 and 90';
    }
    if (form.longitude.trim()) {
      const n = parseFloat(form.longitude);
      if (isNaN(n) || n < -180 || n > 180) errs.longitude = 'Must be between -180 and 180';
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTopError(null);
    if (!validate()) return;

    const payload: CreateSiteRequest = {
      site_identifier: form.site_identifier.trim(),
      name: form.name.trim(),
      status: form.status || undefined,
    };
    if (form.state) payload.state = form.state;
    if (form.city.trim()) payload.city = form.city.trim();
    if (form.county.trim()) payload.county = form.county.trim();
    if (form.address.trim()) payload.address = form.address.trim();
    if (form.latitude.trim()) payload.latitude = parseFloat(form.latitude);
    if (form.longitude.trim()) payload.longitude = parseFloat(form.longitude);
    if (form.aquifer_type) payload.aquifer_type = form.aquifer_type;
    if (form.primary_contaminant) payload.primary_contaminant = form.primary_contaminant;
    if (form.primary_contaminant_other.trim()) {
      payload.primary_contaminant_other = form.primary_contaminant_other.trim();
    }
    if (form.regulatory_program) payload.regulatory_program = form.regulatory_program;

    createMutation.mutate(payload);
  };

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-4 border-b border-mono-800 pb-4">
        <Link to={sitesPath} className="text-mono-500 hover:text-mono-300">
          <ArrowLeftIcon className="h-4 w-4" />
        </Link>
        <div>
          <h1 className="text-lg font-medium text-mono-100 tracking-widest">
            CREATE SITE
          </h1>
          <p className="text-xs text-mono-500 tracking-wider">
            Required fields only — fill in the rest from Site Detail after saving.
          </p>
        </div>
      </div>

      {/* Top-of-form error banner */}
      {topError && (
        <div className="border border-red-900/50 bg-red-950/30 rounded px-3 py-2 text-sm text-red-300">
          {topError}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Identity */}
        <Section title="IDENTITY">
          <Input
            label="Site Identifier"
            required
            value={form.site_identifier}
            onChange={(v) => update('site_identifier', v)}
            error={fieldErrors.site_identifier}
            hint="Short unique code (e.g. SPDB-001). Cannot start with 'PHREATIC-'."
          />
          <Input
            label="Name"
            required
            value={form.name}
            onChange={(v) => update('name', v)}
            error={fieldErrors.name}
          />
          <Select
            label="Status"
            value={form.status}
            onChange={(v) => update('status', v)}
            options={STATUS_OPTIONS}
          />
        </Section>

        {/* Location */}
        <Section title="LOCATION">
          <div className="grid grid-cols-2 gap-4">
            <Select
              label="State"
              value={form.state}
              onChange={(v) => update('state', v)}
              options={US_STATE_OPTIONS}
            />
            <Input
              label="County"
              value={form.county}
              onChange={(v) => update('county', v)}
            />
          </div>
          <Input
            label="City"
            value={form.city}
            onChange={(v) => update('city', v)}
          />
          <Input
            label="Address"
            value={form.address}
            onChange={(v) => update('address', v)}
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Latitude"
              type="number"
              step="0.000001"
              value={form.latitude}
              onChange={(v) => update('latitude', v)}
              error={fieldErrors.latitude}
              hint="Decimal degrees, -90 to 90"
            />
            <Input
              label="Longitude"
              type="number"
              step="0.000001"
              value={form.longitude}
              onChange={(v) => update('longitude', v)}
              error={fieldErrors.longitude}
              hint="Decimal degrees, -180 to 180"
            />
          </div>
        </Section>

        {/* Characteristics */}
        <Section title="CHARACTERISTICS">
          <Select
            label="Aquifer Type"
            value={form.aquifer_type}
            onChange={(v) => update('aquifer_type', v)}
            options={AQUIFER_OPTIONS}
          />
          <Select
            label="Primary Contaminant"
            value={form.primary_contaminant}
            onChange={(v) => update('primary_contaminant', v)}
            options={CONTAMINANT_OPTIONS}
          />
          {form.primary_contaminant === 'other' && (
            <Input
              label="Other (specify)"
              value={form.primary_contaminant_other}
              onChange={(v) => update('primary_contaminant_other', v)}
              hint="e.g. perchlorate, 1,4-dioxane"
            />
          )}
          <Select
            label="Regulatory Program"
            value={form.regulatory_program}
            onChange={(v) => update('regulatory_program', v)}
            options={REGULATORY_PROGRAM_OPTIONS}
          />
        </Section>

        {/* Actions */}
        <div className="flex items-center gap-2 border-t border-mono-800 pt-4">
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="px-4 py-2 text-xs tracking-wider font-medium text-cyan-400 border border-cyan-500/40 rounded hover:bg-cyan-500/10 disabled:opacity-50 transition-colors"
          >
            {createMutation.isPending ? 'CREATING...' : 'CREATE SITE'}
          </button>
          <Link
            to={sitesPath}
            className="px-4 py-2 text-xs tracking-wider text-mono-400 border border-mono-700 rounded hover:bg-mono-800 transition-colors"
          >
            CANCEL
          </Link>
        </div>
      </form>
    </div>
  );
}

// --- Helpers ---------------------------------------------------------

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-3">
      <h2 className="text-xs tracking-widest text-mono-500 border-b border-mono-800 pb-2">
        {title}
      </h2>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Input({
  label,
  required,
  value,
  onChange,
  error,
  hint,
  type = 'text',
  step,
}: {
  label: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  hint?: string;
  type?: string;
  step?: string;
}) {
  return (
    <label className="block">
      <span className="text-xs tracking-wider text-mono-400">
        {label}
        {required && <span className="text-red-400 ml-1">*</span>}
      </span>
      <input
        type={type}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1 w-full bg-mono-950 border ${
          error ? 'border-red-500/60' : 'border-mono-700'
        } rounded px-3 py-2 text-sm text-mono-200 focus:border-cyan-500 focus:outline-none`}
      />
      {error && <span className="block mt-1 text-xs text-red-400">{error}</span>}
      {!error && hint && <span className="block mt-1 text-xs text-mono-600">{hint}</span>}
    </label>
  );
}

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <label className="block">
      <span className="text-xs tracking-wider text-mono-400">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-mono-950 border border-mono-700 rounded px-3 py-2 text-sm text-mono-200 focus:border-cyan-500 focus:outline-none"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}
